/**
 * Alkahest Text-to-Speech provider (OpenAI-compatible)
 */

import { BaseProvider } from '../../core/base-provider';
import {
  TTSOptions,
  AudioResponse,
  Voice,
  UsageStats,
  ProviderError,
  AudioFormat,
} from '../../core/types';

export interface AlkahestTTSProvider extends BaseProvider {
  synthesize(text: string, options?: TTSOptions): Promise<AudioResponse>;
  getVoices(): Promise<Voice[]>;
  stream(text: string, options?: TTSOptions): Promise<ReadableStream<Uint8Array>>;
}

export class AlkahestTTS extends BaseProvider implements AlkahestTTSProvider {
  private baseUrl: string = '';
  private apiKey?: string;
  private voices: Voice[] = [];

  constructor() {
    super('alkahest-tts', 'Alkahest TTS', 'tts');
  }

  protected async onInitialize(): Promise<void> {
    this.baseUrl = this.config.baseUrl || 'http://localhost:9999';
    this.apiKey = this.config.apiKey;

    if (!this.apiKey && !this.config.privyToken) {
      throw new ProviderError('Alkahest API key or Privy token is required', this.id);
    }

    // Fetch available voices
    await this.fetchVoices();
  }

  protected async checkAvailability(): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/audio/voices`, {
        headers: this.getHeaders(),
      });
      return response.ok;
    } catch {
      return false;
    }
  }

  protected async fetchUsage(): Promise<UsageStats> {
    // Same as text provider - check mana balance
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/mana/balance`, {
        headers: this.getHeaders(),
      });

      if (!response.ok) return { tokensUsed: 0, requestsCount: 0, cost: 0 };

      const data = await response.json();
      return {
        remaining: (data as any).balance,
        tokensUsed: 0,
        requestsCount: 0,
        cost: 0,
      };
    } catch {
      return { tokensUsed: 0, requestsCount: 0, cost: 0 };
    }
  }

  private getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (this.apiKey) {
      headers['Authorization'] = `Bearer ${this.apiKey}`;
    } else if (this.config.privyToken) {
      headers['Authorization'] = `Bearer ${this.config.privyToken}`;
    }

    return headers;
  }

  private async fetchVoices(): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/audio/voices`, {
        headers: this.getHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        // Map Alkahest voice format to our Voice interface
        this.voices =
          (data as any).voices?.map((v: any) => ({
            id: v.id || v.voice_id,
            name: v.name,
            gender: v.gender,
            language: v.language || 'en',
            previewUrl: v.preview_url,
            labels: v.labels || {},
          })) || [];
      }
    } catch (error) {
      console.warn('Failed to fetch voices:', error);
      // Fallback to default voices
      this.voices = [
        { id: 'alloy', name: 'Alloy', gender: 'neutral' },
        { id: 'echo', name: 'Echo', gender: 'male' },
        { id: 'fable', name: 'Fable', gender: 'neutral' },
        { id: 'onyx', name: 'Onyx', gender: 'male' },
        { id: 'nova', name: 'Nova', gender: 'female' },
        { id: 'shimmer', name: 'Shimmer', gender: 'female' },
      ];
    }
  }

  async synthesize(text: string, options?: TTSOptions): Promise<AudioResponse> {
    this.ensureInitialized();

    try {
      const format = this.mapAudioFormat(options?.format || 'mp3');

      const response = await fetch(`${this.baseUrl}/api/v1/audio/speech`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({
          model: options?.model || 'tts-1',
          input: text,
          voice: options?.voice || 'alloy',
          response_format: format,
          speed: options?.speed || 1.0,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Unknown error' }));

        if (response.status === 429) {
          throw new ProviderError('Rate limit exceeded', this.id, {
            retryAfter: response.headers.get('X-RateLimit-Reset'),
          });
        }

        if (response.status === 402) {
          throw new ProviderError('Insufficient mana balance', this.id, error);
        }

        throw new ProviderError(
          (error as any).error?.message || (error as any).error || 'Failed to synthesize speech',
          this.id,
          error as any
        );
      }

      const arrayBuffer = await response.arrayBuffer();

      return {
        audio: arrayBuffer,
        format: options?.format || 'mp3',
        voice: options?.voice || 'alloy',
      };
    } catch (error: any) {
      if (error instanceof ProviderError) {
        throw error;
      }

      throw new ProviderError(error.message || 'Failed to synthesize speech', this.id, error);
    }
  }

  async getVoices(): Promise<Voice[]> {
    this.ensureInitialized();
    return this.voices;
  }

  async stream(text: string, options?: TTSOptions): Promise<ReadableStream<Uint8Array>> {
    this.ensureInitialized();

    try {
      const format = this.mapAudioFormat(options?.format || 'mp3');

      const response = await fetch(`${this.baseUrl}/api/v1/audio/speech`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({
          model: options?.model || 'tts-1',
          input: text,
          voice: options?.voice || 'alloy',
          response_format: format,
          speed: options?.speed || 1.0,
          stream: true, // Enable streaming if supported
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new ProviderError(
          (error as any).error?.message || (error as any).error || 'Failed to stream speech',
          this.id,
          error as any
        );
      }

      // Return the response body stream
      if (!response.body) {
        throw new Error('No response body');
      }

      return response.body;
    } catch (error: any) {
      if (error instanceof ProviderError) {
        throw error;
      }

      throw new ProviderError(error.message || 'Failed to stream speech', this.id, error);
    }
  }

  private mapAudioFormat(format: AudioFormat): string {
    // Alkahest supports: mp3, opus, aac, flac
    switch (format) {
      case 'mp3':
      case 'opus':
      case 'aac':
      case 'flac':
        return format;
      case 'wav':
        // Convert to mp3 if wav not supported
        return 'mp3';
      case 'ogg':
        // Use opus for ogg
        return 'opus';
      default:
        return 'mp3';
    }
  }
}
